<script setup>
import VueEasyLightbox from 'vue-easy-lightbox'
import Slider from '@components/Slider.vue'
import {ref, watch} from 'vue'
import { $selectedImageIndex } from '../store'
import { useStore } from '@nanostores/vue'
import { isObjectEmpty } from '@helpers/utils'

const props = defineProps({
  images: {
    type: Array,
    required: true,
  },
  thumbnails: {
    type: Boolean,
    default: true,
  },
})

const sources = [...props.images].filter(image => !image.youtube_url).map(image => image.url)

const imageRef = ref({})
const lightboxIndex = ref(null)
const lightboxVisible = ref(false)

function showProductLightbox(index) {
  lightboxIndex.value = index;
  lightboxVisible.value = true
}

function goToSlide(index) {
  imageRef.value[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
}

function onHide() {
  lightboxVisible.value = false
}

watch(() => useStore($selectedImageIndex).value, value => {
  goToSlide(value)
})
</script>

<template>
  <div>
    <vue-easy-lightbox
        :visible="lightboxVisible"
        :imgs="sources"
        :index="lightboxIndex"
        @hide="onHide"
        :loop="true"
        :rotate-disabled="true"
    ></vue-easy-lightbox>

    <div ref="slider">
      <Slider :sm="1" :md="1" :preview="false" :scroll="false" :dots="true">
        <div
          v-for="(image, i) in images"
          :key="image.id"
          class="product__image"
          :ref="(el) => (imageRef[i] = el)"
        >
          <template v-if="image.youtube_url">
            <iframe loading="lazy" width="560" height="315" :src="image.youtube_url" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
          </template>
          <picture v-else-if="image?.sources && !isObjectEmpty(image.sources)">
            <template v-for="(k, l) in Object.keys(image.sources).reverse()">
              <img
                  v-if="l === Object.keys(image.sources).length - 1"
                  :src="image.sources[k].src"
                  :alt="image.alt"
                  :srcset="image.sources[k].srcSet.attribute"
                  v-bind="image.sources[k].attributes"
                  :fetchpriority="i === 0 ? 'high' : 'auto'"
                  :loading="i === 0 ? 'eager' : 'lazy'"
                  @click="showProductLightbox(i)"
              />
              <source v-else :srcset="image.sources[k].srcSet.attribute" :media="`(min-width: ${k}px)`" />
            </template>
          </picture>
          <img
            v-else
            :src="image.url"
            :alt="image.alt"
            :width="image.width"
            :height="image.height"
            :fetchpriority="i === 0 ? 'high' : 'auto'"
            :loading="i === 0 ? 'eager' : 'lazy'"
            @click="showProductLightbox(i)"
          />
        </div>
      </Slider>
    </div>
    <div class="thumbnails" v-if="images.length > 1 && thumbnails">
      <div
          class="thumbnail-list__item"
          v-for="(image, i) in images"
          :key="image.id"
      >
        <button class="thumbnail" @click="goToSlide(i)">
          <img
              v-if="image?.thumbnail"
              :src="image.thumbnail.src"
              :srcset="image.thumbnail.srcSet.attribute"
              :alt="image.alt"
              v-bind="image.thumbnail.attributes"
          />
          <img
              v-else
              :src="image.url"
              width="100"
              height="100"
              :alt="image.alt"
              loading="lazy"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.slider {
  :deep(.slider__items) {
    padding-top: 0!important;
  }
}

.product__image {
  img, iframe {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 8px;
    cursor: zoom-in;
  }

  iframe {
    height: 100%;
  }
}

.thumbnails {
  display: none;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-y: auto;
  margin: 16px 0;

  @media screen and (min-width: 768px) {
    display: flex;
  }

  .thumbnail-list__item {
    .thumbnail {
      padding: 0;
      border: 0;
      outline: none;
      background-color: #fff;
      cursor: pointer;

      img {
        display: block;
        width: 100px;
        height: 100px;
        object-fit: cover;
        border-radius: 5px;
      }
    }
  }
}
</style>
